import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserByUsername } from '../services/authentikAPI';
import SessionTimer from './SessionTimer';
import { reloadPage } from '../lib/tabCloseSuppression';

// Extracted out of Dashboard.jsx (2026-08-07) — that page's own header
// (logo, Profile/Admin dropdowns, Logout) used to only exist there, so
// navigating into any admin page meant losing access to all three until
// you clicked back to Dashboard first. Self-contained: pulls user/logout
// from AuthContext and does its own small profile fetch, so any page can
// just render <TopBanner /> with no props and no wiring.
//
// Dashboard.jsx itself still keeps its own SEPARATE profile fetch for the
// isPendingApproval gate / accessible-apps filtering below the banner —
// duplicating one cheap GET here is simpler than lifting shared state up
// through every page that wants the banner.

// On Dashboard.jsx, this banner used to sit INSIDE the page's own
// background-image div, so the navy tint blended with the actual BESCOM
// image showing through. On the other pages it's rendered as a sibling
// ABOVE that div (so it can appear before AdminHierarchy.jsx's own loading
// state, etc. — see each page's `<><TopBanner /><div style={page}>`
// pattern), which left it tinting plain (gray) body background instead —
// confirmed via screenshot comparison. Layering the same background image
// directly into this element's own `background` (multiple-background
// syntax: gradient tint on top, image below) fixes that independent of
// where it's mounted. `background-attachment: fixed` matches the shared
// `page` style's own use of it, so the image lines up seamlessly with
// whatever page content sits below the banner, no visible seam.
const glassHeader = {
  background: 'linear-gradient(135deg, rgba(10, 25, 50, 0.55) 0%, rgba(10, 25, 50, 0.35) 100%), url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
};

// Used for the Profile dropdown — needs to stay legible no matter what's
// behind it, so mostly-opaque rather than a true glass effect (just enough
// gradient at the top edge to keep some sheen). The Admin button used to
// open a dropdown of the same sections AdminSidebar.jsx now lists — it's a
// direct navigation button now, landing on a page that keeps that sidebar
// visible so switching sections still feels like one persistent area.
const glassCard = {
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.94) 0%, rgba(255, 255, 255, 0.86) 100%)',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderTop: '1px solid rgba(255, 255, 255, 0.9)',
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
};

// White map-pin with a true cut-out hole (evenodd fill-rule, not a
// separately-colored circle) plus a drop-shadow for depth, matching the
// second reference image the user pasted — the first (glossy red) attempt
// didn't read clearly against this header's own dark background. The hole
// being a genuine transparent cut-out (rather than a solid-colored circle
// guessing at the background color) means it looks right regardless of
// what's actually behind it.
function LocationPinIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))' }}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 2C7.58 2 4 5.58 4 10c0 5.25 7 12 8 12s8-6.75 8-12c0-4.42-3.58-8-8-8zm0 11.2a3.2 3.2 0 1 1 0-6.4 3.2 3.2 0 0 1 0 6.4z"
        fill="#fff"
      />
    </svg>
  );
}

// Standard circular-arrow reload glyph — same "avoid font-dependent
// rendering" reasoning as the pin above, used on the header's own Refresh
// button (see the button's own comment for why it's positioned there).
function RefreshIcon({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <polyline points="21 3 21 9 15 9" />
    </svg>
  );
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

export default function TopBanner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [showProfileDetails, setShowProfileDetails] = useState(false);
  const [webauthnStatus, setWebauthnStatus] = useState(null);
  const profileBtnRef = useRef(null);
  const profilePanelRef = useRef(null);

  // Close the Profile dropdown on a click landing outside both its toggle
  // button and its own panel — mousedown (not click) so this fires before
  // the button's own onClick, letting that onClick's toggle still work
  // normally instead of fighting this listener.
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        showProfileDetails &&
        !profilePanelRef.current?.contains(e.target) &&
        !profileBtnRef.current?.contains(e.target)
      ) {
        setShowProfileDetails(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileDetails]);

  useEffect(() => {
    if (!user?.profile?.preferred_username) return;
    getUserByUsername(user.profile.preferred_username)
      .then((data) => {
        const attributes = data?.attributes || {};
        setProfile({
          name: user.profile?.name || data?.username || user.profile?.preferred_username,
          email: user.profile?.email || '',
          mobile: attributes?.contact_phone || '',
          designation: attributes?.designation || '',
          zone: attributes?.zone || '',
          circle: attributes?.circle || '',
          division: attributes?.division || '',
          subdivision: attributes?.subdivision || '',
          section: attributes?.section || '',
          // Same "most-specific posting location on file" derivation as
          // adminUsers.js's toDirectoryUser (its own `office` field) —
          // walks Zone→Circle→Division→Sub-Division→Section from the
          // deepest level up, taking the first one actually set.
          office: attributes?.section || attributes?.subdivision || attributes?.division || attributes?.circle || attributes?.zone || '',
          profile_complete: attributes?.profile_complete || user.profile?.profile_complete || false,
        });
      })
      .catch((err) => console.error('TopBanner: failed to load profile', err));
  }, [user]);

  // ADDED (2026-09-17): so the Profile panel's passkey button can say
  // whether one's already enrolled instead of always reading "Set up a
  // Passkey" — see accountUpdate.js's /webauthn-status for why this can't
  // just be derived from `user`/`profile` (it needs an Authentik admin-API
  // lookup only the backend can make).
  useEffect(() => {
    if (!user?.profile?.preferred_username) return;
    fetch(`${API_BASE}/account/webauthn-status`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (data) setWebauthnStatus(data); })
      .catch((err) => console.error('TopBanner: failed to load webauthn status', err));
  }, [user]);

  if (!user) return null;

  const userGroups = user.profile?.groups || [];
  const userName = profile?.name || user.profile?.name || user.profile?.preferred_username || 'User';
  // all-apps-admin's admin-section access was revoked 2026-09-10 —
  // bescom-super-admins is now the sole group with any admin-section
  // access anywhere in this app (see adminUsers.js's own comment).
  const isSuperAdmin = userGroups.includes('bescom-super-admins');
  const hasAdminAccess = isSuperAdmin;

  return (
    <div className="tb-header" style={{
      ...glassHeader,
      padding: '16px 32px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      position: 'sticky',
      top: 0,
      zIndex: 100,
    }}>
      {/* Pure inline styles (this file's own convention everywhere else)
          can't respond to viewport size at all — a real CSS media query is
          the only way to make this header wrap/reflow/hide pieces on a
          phone-width screen instead of forcing every element onto one
          unbreakable row that overflows. Scoped to this component's own
          className prefix so nothing else in the app is affected. */}
      <style>{`
        @keyframes topbanner-session-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .tb-header { flex-wrap: wrap; row-gap: 12px; }
        .tb-right { flex-wrap: wrap; }
        @media (max-width: 768px) {
          .tb-header { padding: 12px 16px; }
          .tb-left { gap: 12px !important; }
          .tb-logo { width: 36px !important; }
          .tb-title { font-size: 15px !important; }
          .tb-welcome { font-size: 12px !important; }
          .tb-refresh-label { display: none; }
          .tb-refresh-btn { padding: 8px 10px !important; }
          .tb-right { width: 100%; justify-content: flex-end !important; gap: 8px !important; }
          .tb-btn { padding: 6px 12px !important; font-size: 13px !important; }
          .tb-session-badge { width: 150px !important; }
          .tb-profile-panel { right: 12px !important; left: 12px !important; width: auto !important; top: 84px !important; }
        }
        @media (max-width: 480px) {
          .tb-location { display: none; }
          .tb-session-badge { display: none; }
        }
      `}</style>
      <div className="tb-left" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '16px', cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          <img className="tb-logo" src="/bescom-logo-ui.png" alt="BESCOM" style={{ width: '48px', borderRadius: '8px' }} />
          <div>
            <h1 className="tb-title" style={{ color: '#fff', margin: 0, fontSize: '18px', fontWeight: '700' }}>
              BESCOM Employee Single Sign-On (SSO)
            </h1>
            <p className="tb-welcome" style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '14px' }}>
              Welcome, {userName}
            </p>
            {profile?.office && (
              <p className="tb-location" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'rgba(255,255,255,0.8)', margin: '2px 0 0', fontSize: '13px' }}>
                <LocationPinIcon />
                {profile.office}
              </p>
            )}
          </div>
        </div>
        {/* Placed beside the title, outside the div above's own
            navigate('/dashboard') click handler — a sibling, not nested
            inside it, so clicking Refresh doesn't also trigger that
            navigation via event bubbling. Filled (not the translucent-
            outline style every other header button uses) so it reads as
            its own distinct action rather than blending into
            Profile/Admin/Logout. */}
        <button
          className="tb-refresh-btn"
          type="button"
          onClick={() => reloadPage()}
          title="Refresh this page"
          style={{
            background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
            color: '#fff', border: 'none',
            padding: '8px 16px', borderRadius: '8px',
            fontSize: '13px', cursor: 'pointer', fontWeight: '600',
            display: 'flex', alignItems: 'center', gap: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}>
          <RefreshIcon />
          <span className="tb-refresh-label">Refresh</span>
        </button>
      </div>
      <div className="tb-right" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {user.loginAt && (
          <div className="tb-session-badge" style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '195px', flexShrink: 0 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px',
              background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: '10px',
            }}>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', fontWeight: '500' }}>Session Timer</span>
              <SessionTimer loginAt={user.loginAt} style={{ fontSize: '15px', fontWeight: '700', color: '#fff', fontVariantNumeric: 'tabular-nums' }} />
            </div>
            {/* Scrolling ticker, clipped to the badge's own width above —
                the two facts wouldn't both fit on one line at a readable
                size otherwise, and stacking them as two lines (the
                previous approach) looked cramped/odd against the badge. */}
            <div style={{ overflow: 'hidden' }}>
              <div style={{
                display: 'inline-flex', whiteSpace: 'nowrap',
                animation: 'topbanner-session-marquee 10s linear infinite',
                fontSize: '11px', color: '#fff',
              }}>
                <span style={{ paddingRight: '28px' }}>15 mins Idle-timeout&nbsp;&nbsp;•&nbsp;&nbsp;1 hr Session Timeout</span>
                <span style={{ paddingRight: '28px' }}>15 mins Idle-timeout&nbsp;&nbsp;•&nbsp;&nbsp;1 hr Session Timeout</span>
              </div>
            </div>
          </div>
        )}
        <button
          className="tb-btn"
          ref={profileBtnRef}
          onClick={() => setShowProfileDetails((v) => !v)}
          style={{
            background: 'rgba(255,255,255,0.2)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.4)',
            padding: '8px 18px', borderRadius: '8px',
            fontSize: '14px', cursor: 'pointer', fontWeight: '500'
          }}>
          Profile
        </button>
        {hasAdminAccess && (
          <button
            className="tb-btn"
            onClick={() => {
              setShowProfileDetails(false);
              // Only super admins ever see this button now (hasAdminAccess
              // above), so SSO Statistics — the first sidebar section —
              // is always a section they actually have access to.
              navigate('/admin/sso-stats');
            }}
            style={{
              background: 'rgba(255,255,255,0.2)', color: '#fff',
              border: '1px solid rgba(255,255,255,0.4)',
              padding: '8px 18px', borderRadius: '8px',
              fontSize: '14px', cursor: 'pointer', fontWeight: '500'
            }}>
            Admin
          </button>
        )}
        <button
          className="tb-btn"
          // CONFIRMED BUG (2026-09-09): logout was passed directly as the
          // handler here — React calls any onClick handler with the click's
          // SyntheticEvent as its first argument, which became logout's own
          // `reason` param the moment that param was added (for the idle
          // timeout label). A truthy `reason` made logout() try to
          // JSON.stringify(the click event) — circular references, throws
          // — silently swallowed by logout()'s own catch(_){}, so
          // /account/end-session never actually got called at all: no
          // cookie cleared, no Authentik session ended, nothing recorded.
          // The rest of logout() still ran (local cleanup + redirect to
          // '/'), so it LOOKED like a real logout right up until the still
          // fully-valid cookie silently logged the person back in on the
          // next page load. Wrapping in an arrow function here (matching
          // every other button in this file already) drops the event
          // instead of forwarding it.
          onClick={() => logout()}
          style={{
            background: 'rgba(255,255,255,0.2)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.4)',
            padding: '8px 20px', borderRadius: '8px',
            fontSize: '14px', cursor: 'pointer', fontWeight: '500'
          }}>
          Logout
        </button>
      </div>

      {showProfileDetails && profile && (
        <div
          className="tb-profile-panel"
          ref={profilePanelRef}
          style={{
          ...glassCard,
          position: 'absolute', top: '88px', right: '32px',
          width: '320px', color: '#111',
          padding: '20px', zIndex: 50
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <strong style={{ fontSize: '14px' }}>User Profile</strong>
            <button
              onClick={() => setShowProfileDetails(false)}
              style={{ background: 'transparent', border: 'none', color: '#666', cursor: 'pointer', fontSize: '16px' }}>
              ×
            </button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Name</p>
            <p style={{ margin: '4px 0 0', fontSize: '15px', fontWeight: '600' }}>{profile.name || userName}</p>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Email</p>
            <p style={{ margin: '4px 0 0', fontSize: '15px' }}>{profile.email || 'Not available'}</p>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Mobile</p>
            <p style={{ margin: '4px 0 0', fontSize: '15px' }}>{profile.mobile || 'Not available'}</p>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Designation</p>
            <p style={{ margin: '4px 0 0', fontSize: '15px' }}>{profile.designation || 'Not set'}</p>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Working Place</p>
            <p style={{ margin: '4px 0 0', fontSize: '15px' }}>{profile.office || 'Not set'}</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Zone/Office</p>
              <p style={{ margin: '4px 0 0', fontSize: '15px' }}>{profile.zone || '-'}</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Circle</p>
              <p style={{ margin: '4px 0 0', fontSize: '15px' }}>{profile.circle || '-'}</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Division</p>
              <p style={{ margin: '4px 0 0', fontSize: '15px' }}>{profile.division || '-'}</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Subdivision</p>
              <p style={{ margin: '4px 0 0', fontSize: '15px' }}>{profile.subdivision || '-'}</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Section</p>
              <p style={{ margin: '4px 0 0', fontSize: '15px' }}>{profile.section || '-'}</p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>Profile</p>
              <p style={{ margin: '4px 0 0', fontSize: '15px', color: profile.profile_complete ? '#0F6E56' : '#B45C00' }}>
                {profile.profile_complete ? 'Complete' : 'Incomplete'}
              </p>
            </div>
          </div>

          {(!profile?.mobile || !profile?.email) && (
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {!profile?.mobile && (
                <button
                  onClick={() => navigate('/add-phone')}
                  style={{
                    background: '#F0F4F8', color: '#0072B1',
                    border: '1px solid #D3E3ED',
                    padding: '8px 14px', borderRadius: '8px',
                    fontSize: '13px', cursor: 'pointer', fontWeight: '500',
                    textAlign: 'left'
                  }}>
                  📱 Add Phone Number
                </button>
              )}
              {!profile?.email && (
                <button
                  onClick={() => navigate('/add-email')}
                  style={{
                    background: '#F0F4F8', color: '#0072B1',
                    border: '1px solid #D3E3ED',
                    padding: '8px 14px', borderRadius: '8px',
                    fontSize: '13px', cursor: 'pointer', fontWeight: '500',
                    textAlign: 'left'
                  }}>
                  ✉️ Add Email Address
                </button>
              )}
            </div>
          )}

          {/* The button itself stays unconditional — unlike the Add Phone/
              Add Email section above (which hides once both are filled in),
              this should always be reachable: someone with an existing
              passkey may still want to add another (e.g. a second device),
              and someone without one yet should always be able to start.
              TOTP stays the only mandatory factor; this is purely additive.
              Only the LABEL changes based on webauthnStatus, fetched above —
              null while loading (button just reads "Set up a Passkey" as a
              safe default), then reflects the real enrolled count. */}
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
            {webauthnStatus?.enrolled && (
              <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#0F6E56', fontWeight: '600' }}>
                ✓ Passkey enrolled{webauthnStatus.count > 1 ? ` (${webauthnStatus.count})` : ''}
              </p>
            )}
            <button
              onClick={() => navigate('/setup-passkey')}
              style={{
                background: '#F0F4F8', color: '#0072B1',
                border: '1px solid #D3E3ED',
                padding: '8px 14px', borderRadius: '8px',
                fontSize: '13px', cursor: 'pointer', fontWeight: '500',
                textAlign: 'left', width: '100%',
              }}>
              🔑 {webauthnStatus?.enrolled ? 'Add Another Passkey' : 'Set up a Passkey'}
            </button>
            {/* ADDED (2026-09-18): only shown once at least one passkey
                exists — nothing to manage otherwise. Self-service delete,
                so accumulated duplicates (see this session's own history)
                no longer need an admin to clean up. */}
            {webauthnStatus?.enrolled && (
              <button
                onClick={() => navigate('/manage-passkeys')}
                style={{
                  background: 'none', color: '#666',
                  border: 'none',
                  padding: '6px 0 0', fontSize: '12px', cursor: 'pointer', fontWeight: '500',
                  textAlign: 'left', width: '100%', textDecoration: 'underline',
                }}>
                Manage your Passkeys
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
